import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { useOnErrorAuto } from '@/utils/hooks/error-handling'
import { useMutation } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { SET_REST_PERIOD_PAID_MUTATION } from '../../mutations'
import type { ApprovalRestPeriodNode } from '../../types.graphql'

/**
 * Leaves one daily rest unpaid when its salary work time pays daily rests.
 * Renders nothing for rests that have no daily rest salary code.
 */
export const RestPaidSwitch: React.FC<{
  restPeriod: ApprovalRestPeriodNode
  canChangePaid: boolean
  onChange: () => void
  className?: string
}> = ({ restPeriod, canChangePaid, onChange, className }) => {
  const onErrorAuto = useOnErrorAuto()
  const translations = useTranslate({
    paid: 'common.paid',
    unpaid: 'common.unpaid',
  })
  const [setPaid, { loading }] = useMutation(SET_REST_PERIOD_PAID_MUTATION, {
    onCompleted: onChange,
    onError: onErrorAuto(),
  })
  const dailyRestSalaryCode =
    restPeriod.restType === 'DAILY'
      ? restPeriod.salarySetting?.dailyRestSalaryCode
      : null

  if (!dailyRestSalaryCode) return null

  return (
    <label className={cn('flex items-center gap-2 font-sans', className)}>
      <span className={restPeriod.paid ? '' : 'line-through'}>
        {dailyRestSalaryCode.name}
      </span>
      <Switch
        checked={restPeriod.paid}
        disabled={!canChangePaid || loading}
        onCheckedChange={paid =>
          void setPaid({
            variables: { restPeriod: restPeriod.id, paid },
          })
        }
      />
      <span className="w-14">
        {restPeriod.paid ? translations.paid : translations.unpaid}
      </span>
    </label>
  )
}
